/**
 * Chart Template Save Dialog
 * Triggered by Ctrl+S (or the template panel UI). Serializes the current
 * chart state (type, indicators, timezone, brickSize if applicable) and
 * stores it in Dexie. Overlay coordinates are NOT saved.
 */
import { useCallback, useRef, useState } from "react";
import { Save } from "lucide-react";
import { useShallow } from "zustand/react/shallow";

import * as Dialog from "@radix-ui/react-dialog";
import { useChartStore } from "@/stores/chartStore";
import { useSettingsStore } from "@/stores/settingsStore";
import { db, type ChartTemplate } from "@/lib/lytick/db";

interface ChartTemplateSaveDialogProps {
  open: boolean;
  onClose: () => void;
  /** Optional: DOM reference to the chart for getStyles() snapshot */
  chartRef?: React.RefObject<unknown>;
}

function generateId(): string {
  return `tpl-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function ChartTemplateSaveDialog({ open, onClose, chartRef }: ChartTemplateSaveDialogProps) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { chartType, indicators } = useChartStore(
    useShallow((s) => ({ chartType: s.chartType, indicators: s.indicators }))
  );
  const timezone = useSettingsStore((s) => s.chartAppearance.timezone);

  const handleSave = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Template name is required.");
      inputRef.current?.focus();
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Optional: snapshot chart styles if the API is available
      const chart = chartRef?.current as { getStyles?: () => object } | null;
      const styles = chart?.getStyles ? chart.getStyles() : undefined;

      const template: ChartTemplate = {
        id: generateId(),
        name: trimmedName,
        createdAt: Date.now(),
        chartType,
        indicators: [...indicators],
        timezone: timezone || "UTC",
        styles,
      };

      await db.chartTemplates.put(template);
      setName("");
      onClose();
    } catch (err) {
      setError("Failed to save template. Please try again.");
      console.error("[LYTICK] Template save failed:", err);
    } finally {
      setSaving(false);
    }
  }, [name, chartType, indicators, timezone, chartRef, onClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSave();
    }
    if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[340px] -translate-x-1/2 -translate-y-1/2 rounded-xl border p-5 shadow-2xl"
          style={{
            backgroundColor: "var(--bg-card)",
            borderColor: "var(--border-default)",
          }}
        >
          <Dialog.Title
            className="mb-1 text-sm font-semibold"
            style={{ color: "var(--text-primary)" }}
          >
            Save Chart Template
          </Dialog.Title>
          <Dialog.Description
            className="mb-4 text-xs"
            style={{ color: "var(--text-secondary)" }}
          >
            Saves chart type, indicators, and timezone. Drawings are not included.
          </Dialog.Description>

          <div className="mb-4">
            <input
              ref={inputRef}
              value={name}
              onChange={(e) => { setName(e.target.value); setError(null); }}
              onKeyDown={handleKeyDown}
              placeholder="Template name…"
              autoFocus
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
              style={{
                backgroundColor: "var(--bg-elevated)",
                borderColor: error ? "var(--color-loss)" : "var(--border-default)",
                color: "var(--text-primary)",
              }}
            />
            {error && (
              <p className="mt-1 text-xs" style={{ color: "var(--color-loss)" }}>{error}</p>
            )}
          </div>

          <div className="mb-3 space-y-1 text-xs" style={{ color: "var(--text-secondary)" }}>
            <p>Chart type: <span style={{ color: "var(--text-primary)" }}>{chartType}</span></p>
            <p>Indicators: <span style={{ color: "var(--text-primary)" }}>{indicators.length > 0 ? indicators.join(", ") : "None"}</span></p>
            <p>Timezone: <span style={{ color: "var(--text-primary)" }}>{timezone || "UTC"}</span></p>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border py-2 text-xs transition-colors hover:bg-surface-hover"
              style={{ borderColor: "var(--border-default)", color: "var(--text-secondary)" }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium transition-all active:scale-95 disabled:opacity-50"
              style={{ backgroundColor: "var(--accent-primary)", color: "#fff" }}
            >
              <Save className="size-3.5" />
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
